import type { W_JobTracking_CreateJobTrackingTemplate_RM } from 'ab-api/web/jobTracking/createJobTrackingTemplate';
import type { W_JobTracking_EditJobTrackingTemplate_RM } from 'ab-api/web/jobTracking/editJobTrackingTemplate';
import type { W_JobTracking_FindById_VM } from 'ab-api/web/jobTracking/findTemplate';

import type { JobTrackingTemplateColumnType } from '@acceligentllc/shared/enums/jobTrackingTemplateColumnType';

type ClientId = string;

export class ColumnDataSourceFM {
	_cid: ClientId; // UI-only key
	id?: number;
	field: {
		reportTypeId: number;
		reportBlockId: number;
		reportBlockFieldId: number;
	};
}

export class TemplateColumnFM {
	_cid: ClientId;
	id?: number;
	name: string;
	type: JobTrackingTemplateColumnType;
	index: number;
	isDefinition: boolean;
	excludedFieldReportIds: number[];
	dataSources: ColumnDataSourceFM[];
}

export default class FormModel {
	id?: number;
	name: string;
	description: Nullable<string>;
	columns: TemplateColumnFM[];

	constructor(jobTrackingTemplate: W_JobTracking_FindById_VM) {
		this.id = jobTrackingTemplate.id;
		this.name = jobTrackingTemplate.name;
		this.description = jobTrackingTemplate.description;

		this.columns = (jobTrackingTemplate.columns ?? []).map(FormModel.mapColumnFromVM);
	}

	static fromVM(vm: W_JobTracking_FindById_VM): FormModel {
		return new FormModel(vm);
	}

	private static mapColumnFromVM(c: W_JobTracking_FindById_VM['columns'][number]): TemplateColumnFM {
		return {
			_cid: `${c.id}`,
			id: c.id,
			name: c.name,
			type: c.type,
			index: c.index,
			isDefinition: c.isDefinition,
			excludedFieldReportIds: c.excludedFieldReportIds ?? [],
			dataSources: (c.dataSources ?? []).map(FormModel.mapDataSourceFromVM),
		};
	}

	private static mapDataSourceFromVM(
		ds: W_JobTracking_FindById_VM['columns'][number]['dataSources'][number]
	): ColumnDataSourceFM {
		return {
			_cid: `${ds.id}`,
			id: ds.id,
			field: {
				reportTypeId: ds.reportTypeId,
				reportBlockId: ds.reportBlockId,
				reportBlockFieldId: ds.reportBlockFieldId,
			},
		};
	}

	static toCreateRM(form: FormModel): W_JobTracking_CreateJobTrackingTemplate_RM {
		return {
			name: form.name.trim(),
			description: form.description?.trim() ?? null,
			columns: (form.columns ?? []).map((col) => ({
				name: col.name.trim(),
				type: col.type,
				index: col.index,
				isDefinition: col.isDefinition,
				excludedFieldReportIds: col.excludedFieldReportIds ?? [],
				dataSources: (col.dataSources ?? []).map((ds) => ({
					field: {
						reportTypeId: ds.field.reportTypeId,
						reportBlockId: ds.field.reportBlockId,
						reportBlockFieldId: ds.field.reportBlockFieldId,
					},
				})),
			})),
		};
	}

	static toEditRM(form: FormModel): W_JobTracking_EditJobTrackingTemplate_RM {
		return {
			name: form.name.trim(),
			description: form.description?.trim() ?? null,
			columns: (form.columns.filter((col) => !!col._cid) ?? []).map((col) => ({
				id: col.id,
				name: col.name.trim(),
				type: col.type,
				index: col.index,
				isDefinition: col.isDefinition,
				excludedFieldReportIds: col.excludedFieldReportIds ?? [],
				dataSources: (col.dataSources ?? []).map((ds) => ({
					id: ds.id,
					field: {
						reportTypeId: ds.field.reportTypeId,
						reportBlockId: ds.field.reportBlockId,
						reportBlockFieldId: ds.field.reportBlockFieldId,
					},
				})),
			})),
		};
	}

}
